import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { saveAs } from 'file-saver';
import Modal from '../../modules/Modal';
import { ContainedButton } from '../../modules/Buttons';
import styles from './TranscodedFeedsCSVModal.module.scss';
import { getTokenFromLocalStorage } from '../../../helpers/localStorage';

type Props = {
  open: boolean;
  onClose: () => void;
  src: string;
  title?: string;
  showControls?: boolean;
  downloadFilename?: string;
};

function parseCSV(csv: string): string[][] {
  if (csv.charCodeAt(0) === 0xfeff) csv = csv.slice(1);

  const out: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let q = false;

  for (let i = 0; i < csv.length; i++) {
    const c = csv[i];
    if (q) {
      if (c === '"') {
        if (csv[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          q = false;
        }
      } else {
        cur += c;
      }
    } else {
      if (c === '"') q = true;
      else if (c === ',') {
        row.push(cur);
        cur = '';
      } else if (c === '\n') {
        row.push(cur.replace(/\r$/, ''));
        out.push(row);
        row = [];
        cur = '';
      } else {
        cur += c;
      }
    }
  }
  row.push(cur.replace(/\r$/, ''));
  out.push(row);

  // drop trailing empty row if present
  if (out.length && out[out.length - 1].length === 1 && out[out.length - 1][0] === '') out.pop();
  return out;
}

/** Ensure header keys are unique even if CSV has duplicates (e.g., created_at twice) */
function uniqueHeaders(headers: string[]): string[] {
  const seen: Record<string, number> = {};
  return headers.map((h) => {
    const key = h || 'column';
    const count = (seen[key] || 0) + 1;
    seen[key] = count;
    return count === 1 ? key : `${key}_${count}`;
  });
}

type SortDir = 'asc' | 'desc';
type SortState = { key: string; dir: SortDir } | null;

function isNumericLike(v: string) {
  return v !== '' && !Number.isNaN(Number(v));
}
function isDateLike(v: string) {
  // quick check for yyyy-mm-dd... or ISO-ish; adjust if you format dates differently
  return /\d{4}-\d{2}-\d{2}/.test(v);
}
function sortValue(v: string) {
  if (isNumericLike(v)) return Number(v);
  if (isDateLike(v)) return new Date(v).getTime() || v;
  return v.toLowerCase();
}

export default function TranscodedFeedsCSVModal({
  open,
  onClose,
  src,
  title = 'Transcoded videofeeds (CSV)',
  showControls = true,
  downloadFilename,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortState>(null);

  const closeRef = useRef<HTMLDivElement>(null);

  useHotkeys('Enter', () => {
    if (open && !loading) closeRef.current?.click();
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const token = getTokenFromLocalStorage();
        const res = await fetch(src, {
          method: 'GET',
          headers: {
            Accept: 'text/csv',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const text = await res.text();
        const parsed = parseCSV(text);
        if (!parsed.length) {
          setHeaders([]);
          setRows([]);
          return;
        }
        const unique = uniqueHeaders(parsed[0]);
        const body = parsed.slice(1).map((r) => {
          // pad/truncate to match header length
          if (r.length < unique.length) return r.concat(Array(unique.length - r.length).fill(''));
          if (r.length > unique.length) return r.slice(0, unique.length);
          return r;
        });
        setHeaders(unique);
        setRows(body);
      } catch (e: any) {
        setError(e?.message || 'Failed to load CSV');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, src]);

  const [onlyDone, setOnlyDone] = useState(true);

  const filteredRows = useMemo(() => {
    if (!showControls) return rows;
    if (!onlyDone) return rows;
    const iStatus = headers.indexOf('status');
    const iPre = headers.indexOf('pre_delete_status');
    if (iStatus < 0 && iPre < 0) return rows;

    return rows.filter((r) => {
      const s = (r[iStatus] || '').toUpperCase();
      const p = (r[iPre] || '').toUpperCase();
      return s === 'DONE' || p === 'DONE';
    });
  }, [rows, headers, onlyDone, showControls]);

  const sortedRows = useMemo(() => {
    const base = filteredRows;
    if (!sort) return base;
    const idx = headers.indexOf(sort.key);
    if (idx < 0) return base;
    const copy = [...base];
    copy.sort((a, b) => {
      const av = sortValue(a[idx] ?? '');
      const bv = sortValue(b[idx] ?? '');
      if (av < bv) return sort.dir === 'asc' ? -1 : 1;
      if (av > bv) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredRows, sort, headers]);

  const onSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortDirFor = (key: string): SortDir | undefined => (sort?.key === key ? sort.dir : undefined);

  const totals = useMemo(() => {
    if (!headers.length || !rows.length) return { hours: 0, minutes: 0 };

    const idxStatus = headers.indexOf('status');
    const idxPreDel = headers.indexOf('pre_delete_status');
    const idxSecs = headers.indexOf('total_duration_seconds');
    const idxMins = headers.indexOf('total_duration_in_minutes');
    const idxHours = headers.indexOf('total_duration_in_hours');

    let totalSeconds = 0;

    for (const r of rows) {
      const status = (r[idxStatus] || '').toUpperCase();
      const pre = (r[idxPreDel] || '').toUpperCase();
      if (status === 'DONE' || pre === 'DONE') {
        if (idxSecs !== -1) {
          const s = Number(r[idxSecs] || 0);
          if (Number.isFinite(s)) totalSeconds += s;
        } else if (idxMins !== -1) {
          const m = Number(r[idxMins] || 0);
          if (Number.isFinite(m)) totalSeconds += m * 60;
        } else if (idxHours !== -1) {
          const h = Number(r[idxHours] || 0);
          if (Number.isFinite(h)) totalSeconds += h * 3600;
        }
      }
    }

    const hours = totalSeconds / 3600;
    const minutes = totalSeconds / 60;
    return { hours, minutes };
  }, [headers, rows]);

  const handleDownload = async () => {
    try {
      setLoading(true);
      const token = getTokenFromLocalStorage();
      const res = await fetch(src, {
        method: 'GET',
        headers: {
          Accept: 'text/csv',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      const blob = await res.blob();
      saveAs(blob, downloadFilename || `${title}.csv`);
    } catch (e: any) {
      setError(e?.message || 'Failed to download CSV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>
          {title}
          {!!sortedRows.length && <span className={styles.count}>{sortedRows.length}</span>}
          {showControls && (
            <label className={styles.checkboxWrap}>
              <input type="checkbox" checked={onlyDone} onChange={(e) => setOnlyDone(e.target.checked)} />
              <span>Show only transcoded (DONE)</span>
            </label>
          )}
        </div>

        {showControls && (
          <div className={styles.subtitleTotals}>
            <span>
              Total transcoded hours: <strong>{totals.hours.toFixed(1)}</strong> ({totals.minutes.toFixed(1)} min)
            </span>
          </div>
        )}

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h}>
                    <button type="button" className={styles.sortBtn} onClick={() => onSort(h)} aria-label={`Sort by ${h}`}>
                      <span className={styles.thLabel}>{h}</span>
                      <span
                        className={`${styles.sortIcon} ${
                          sortDirFor(h) === 'asc' ? styles.asc : sortDirFor(h) === 'desc' ? styles.desc : ''
                        }`}
                      />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className={styles.skeletonRow}>
                    <td colSpan={headers.length}></td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={headers.length} className={styles.emptyCell}>
                    {error}
                  </td>
                </tr>
              ) : !rows.length ? (
                <tr>
                  <td colSpan={headers.length} className={styles.emptyCell}>
                    No data
                  </td>
                </tr>
              ) : (
                sortedRows.map((r, i) => (
                  <tr key={i}>
                    {r.map((cell, j) => (
                      <td key={j} className={headers[j] === 'id' ? styles.idCell : ''}>
                        {cell || '—'}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.actions}>
          <ContainedButton onClick={handleDownload} kind="purple">
            <div>Download CSV</div>
          </ContainedButton>
          <ContainedButton onClick={onClose} kind="purple">
            <div ref={closeRef}>Close</div>
          </ContainedButton>
        </div>
      </div>
    </Modal>
  );
}
